package com.havn.app.features.attachments.presentation

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInHorizontally
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonListFooter
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.havnNeedsNativeTabBarContentInset
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.domain.HavnAttachmentRecipientSource
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun FilesScreen(
    state: AttachmentsUiState,
    shareRecipients: List<HavnAttachmentRecipient>,
    recentShareRecipients: List<HavnAttachmentRecipient>,
    isLoadingShareRecipients: Boolean = false,
    isLoadingMoreShareRecipients: Boolean = false,
    hasMoreShareRecipients: Boolean = false,
    onBackToToday: (() -> Unit)? = null,
    onLoadFiles: () -> Unit,
    onOpenFile: (HavnAttachment) -> Unit,
    onOpenShareRecipients: () -> Unit = {},
    onShareRecipientQueryChange: (String) -> Unit = {},
    onLoadMoreShareRecipients: () -> Unit = {},
    onSendEmail: (List<HavnAttachmentRecipient>, List<HavnAttachment>) -> Unit,
    onSendSms: (List<HavnAttachmentRecipient>, List<HavnAttachment>) -> Unit,
    onDeleteFile: (HavnAttachment) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    var searchQuery by remember { mutableStateOf("") }
    var selectedIds by remember(state.attachments) { mutableStateOf(emptySet<String>()) }
    var pendingDeleteFiles by remember { mutableStateOf<List<HavnAttachment>>(emptyList()) }
    var contactPickerMode by remember { mutableStateOf<FileContactPickerMode?>(null) }
    var recipientQuery by remember { mutableStateOf("") }
    var recipientFilter by remember { mutableStateOf(FileRecipientFilter.All) }
    var selectedRecipientIds by remember(contactPickerMode) { mutableStateOf(emptySet<String>()) }

    LaunchedEffect(Unit) {
        onLoadFiles()
    }

    val selectedFiles = remember(state.attachments, selectedIds) {
        state.attachments.filter { it.id in selectedIds }
    }
    val canDeleteSelected = selectedFiles.isNotEmpty() &&
        selectedFiles.all { it.uploadedByAgentId == state.currentAgentId }
    val files = remember(state.attachments, searchQuery) {
        state.attachments
            .filter { it.matchesFileSearch(searchQuery) }
            .sortedWith(compareByDescending<HavnAttachment> { it.updatedAt ?: it.createdAt.orEmpty() })
    }

    if (pendingDeleteFiles.isNotEmpty()) {
        AlertDialog(
            onDismissRequest = { pendingDeleteFiles = emptyList() },
            title = { Text("Delete selected files?") },
            text = { Text("This will remove ${pendingDeleteFiles.size} selected file${if (pendingDeleteFiles.size == 1) "" else "s"} from HAVN storage.") },
            confirmButton = {
                TextButton(
                    onClick = {
                        val filesToDelete = pendingDeleteFiles
                        pendingDeleteFiles = emptyList()
                        selectedIds = selectedIds - filesToDelete.map { it.id }.toSet()
                        filesToDelete.forEach(onDeleteFile)
                    }
                ) {
                    Text("Delete", color = theme.danger)
                }
            },
            dismissButton = {
                TextButton(onClick = { pendingDeleteFiles = emptyList() }) {
                    Text("Cancel")
                }
            }
        )
    }

    contactPickerMode?.let { mode ->
        Dialog(
            onDismissRequest = { contactPickerMode = null },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            Surface(color = theme.background, modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize().padding(HavnSpacing.lg)) {
                    AnimatedVisibility(
                        visible = true,
                        enter = slideInHorizontally(animationSpec = tween(260), initialOffsetX = { it }) + fadeIn(animationSpec = tween(220))
                    ) {
                        FileRecipientPickerScreen(
                            mode = mode,
                            query = recipientQuery,
                            selectedFilter = recipientFilter,
                            selectedRecipientIds = selectedRecipientIds,
                            allRecipients = shareRecipients,
                            recentRecipients = recentShareRecipients,
                            isLoading = isLoadingShareRecipients,
                            isLoadingMore = isLoadingMoreShareRecipients,
                            hasMoreRecipients = hasMoreShareRecipients,
                            onQueryChange = {
                                recipientQuery = it
                                onShareRecipientQueryChange(it)
                            },
                            onFilterChange = { recipientFilter = it },
                            onBack = { contactPickerMode = null },
                            onToggleRecipient = { id -> selectedRecipientIds = selectedRecipientIds.toggle(id) },
                            onLoadMoreRecipients = onLoadMoreShareRecipients,
                            onSend = { recipients ->
                                contactPickerMode = null
                                when (mode) {
                                    FileContactPickerMode.Email -> onSendEmail(recipients, selectedFiles)
                                    FileContactPickerMode.Sms -> onSendSms(recipients, selectedFiles)
                                }
                            }
                        )
                    }
                }
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
    ) {
        val bottomContentPadding = if (havnNeedsNativeTabBarContentInset) 132.dp else 24.dp
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 22.dp),
            contentPadding = PaddingValues(top = 24.dp, bottom = bottomContentPadding),
            verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            item("files-header") {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                    onBackToToday?.let { onBack ->
                        Row(
                            modifier = Modifier
                                .clickable(onClick = onBack)
                                .padding(vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            HavnIcon(HavnIconKey.Back, contentDescription = "Today", tint = theme.primaryAccent, modifier = Modifier.size(24.dp))
                            Text("Today", color = theme.primaryAccent, style = MaterialTheme.typography.titleMedium)
                        }
                    }
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            "My Files",
                            color = theme.primaryText,
                            style = HavnTopHeaderTextStyle,
                            modifier = Modifier.weight(1f)
                        )
                        if (selectedFiles.isNotEmpty()) {
                            FilesIconCircleButton(
                                icon = HavnIconKey.Mail,
                                contentDescription = "Send selected files by email",
                                onClick = {
                                    recipientQuery = ""
                                    onOpenShareRecipients()
                                    contactPickerMode = FileContactPickerMode.Email
                                }
                            )
                            FilesIconCircleButton(
                                icon = HavnIconKey.Message,
                                contentDescription = "Send selected files by SMS",
                                onClick = {
                                    recipientQuery = ""
                                    onOpenShareRecipients()
                                    contactPickerMode = FileContactPickerMode.Sms
                                }
                            )
                            FilesIconCircleButton(
                                icon = HavnIconKey.Trash,
                                contentDescription = "Delete selected files",
                                onClick = { pendingDeleteFiles = selectedFiles },
                                enabled = canDeleteSelected,
                                danger = true
                            )
                        }
                    }
                }
            }

            item("files-search") {
                HavnSearchField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    placeholder = "Search files"
                )
            }

            state.errorMessage?.let { error ->
                item("files-error") {
                    Text(error, color = theme.danger, style = MaterialTheme.typography.bodyMedium)
                }
            }

            when {
                state.isLoading && state.attachments.isEmpty() -> {
                    items(6, key = { "file-loading-$it" }) {
                        FileSkeletonRow()
                    }
                }
                files.isEmpty() -> {
                    item("files-empty") {
                        EmptyState(
                            title = if (searchQuery.isBlank()) "No files yet" else "No files found",
                            body = if (searchQuery.isBlank()) "Files attached to properties and tasks will appear here." else "Try another filename or context."
                        )
                    }
                }
                else -> {
                    items(files, key = { it.id }) { file ->
                        val selected = file.id in selectedIds
                        FileListRow(
                            file = file,
                            selected = selected,
                            onSelectedChanged = {
                                selectedIds = if (selected) selectedIds - file.id else selectedIds + file.id
                            },
                            onOpen = { onOpenFile(file) }
                        )
                    }
                }
            }

            item("files-bottom-space") {
                Spacer(Modifier.height(0.dp))
            }
        }
    }
}

@Composable
private fun FilesIconCircleButton(
    icon: HavnIconKey,
    contentDescription: String,
    onClick: () -> Unit,
    enabled: Boolean = true,
    danger: Boolean = false
) {
    val theme = LocalHavnThemeTokens.current
    val tint = when {
        !enabled -> theme.secondaryText.copy(alpha = 0.42f)
        danger -> theme.danger
        else -> theme.primaryAccent
    }
    Surface(
        modifier = Modifier
            .size(42.dp)
            .clickable(enabled = enabled, onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, if (danger && enabled) theme.danger.copy(alpha = 0.44f) else theme.border)
    ) {
        Box(contentAlignment = Alignment.Center) {
            HavnIcon(icon, contentDescription = contentDescription, tint = tint, modifier = Modifier.size(20.dp))
        }
    }
}

@Composable
private fun FileListRow(
    file: HavnAttachment,
    selected: Boolean,
    onSelectedChanged: () -> Unit,
    onOpen: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent.copy(alpha = 0.8f) else theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onOpen)
                .padding(horizontal = 12.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Checkbox(
                checked = selected,
                onCheckedChange = { onSelectedChanged() },
                colors = CheckboxDefaults.colors(
                    checkedColor = theme.primaryAccent,
                    uncheckedColor = theme.border,
                    checkmarkColor = theme.background
                )
            )
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .background(theme.primaryAccent.copy(alpha = 0.14f), MaterialTheme.shapes.medium),
                contentAlignment = Alignment.Center
            ) {
                HavnIcon(HavnIconKey.Note, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    file.fileName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    listOf(file.contextLabel(), file.createdAt.shortDateLabel(), file.sizeLabel())
                        .filter { it.isNotBlank() }
                        .joinToString(" · "),
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
        }
    }
}

@Composable
private fun FileSkeletonRow() {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .height(72.dp),
        color = theme.card.copy(alpha = 0.6f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.5f))
    ) {}
}

@Composable
private fun FileRecipientPickerScreen(
    mode: FileContactPickerMode,
    query: String,
    selectedFilter: FileRecipientFilter,
    selectedRecipientIds: Set<String>,
    allRecipients: List<HavnAttachmentRecipient>,
    recentRecipients: List<HavnAttachmentRecipient>,
    isLoading: Boolean = false,
    isLoadingMore: Boolean = false,
    hasMoreRecipients: Boolean = false,
    onQueryChange: (String) -> Unit,
    onFilterChange: (FileRecipientFilter) -> Unit,
    onBack: () -> Unit,
    onToggleRecipient: (String) -> Unit,
    onLoadMoreRecipients: () -> Unit = {},
    onSend: (List<HavnAttachmentRecipient>) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val allAvailableRecipients = (allRecipients + recentRecipients).dedupeRecipients()
    val pool = when (selectedFilter) {
        FileRecipientFilter.All -> allRecipients
        FileRecipientFilter.Recent -> recentRecipients
    }.dedupeRecipients()
    val normalizedQuery = query.trim()
    val recipients = pool
        .filter { recipient ->
            normalizedQuery.isBlank() ||
                recipient.displayName.contains(normalizedQuery, ignoreCase = true) ||
                recipient.email.orEmpty().contains(normalizedQuery, ignoreCase = true) ||
                recipient.phone.orEmpty().contains(normalizedQuery, ignoreCase = true)
        }
        .filter { recipient ->
            if (mode == FileContactPickerMode.Email) !recipient.email.isNullOrBlank() else !recipient.phone.isNullOrBlank()
        }
    val selectedRecipients = allAvailableRecipients.filter { it.id in selectedRecipientIds }

    Column(modifier = Modifier.fillMaxSize(), verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnIcon(HavnIconKey.Back, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp).clickable(onClick = onBack))
            Text(
                if (mode == FileContactPickerMode.Email) "Send Email" else "Send SMS",
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineSmall,
                modifier = Modifier.weight(1f)
            )
        }
        HavnSearchField(value = query, onValueChange = onQueryChange, placeholder = "Search contacts")
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            FileRecipientFilter.entries.forEach { filter ->
                HavnListFilterChip(
                    label = filter.label,
                    selected = selectedFilter == filter,
                    onClick = { onFilterChange(filter) },
                    modifier = Modifier.weight(1f)
                )
            }
        }
        if (isLoading && recipients.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = theme.primaryAccent)
            }
        } else if (recipients.isEmpty()) {
            Text("No contacts found", color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxWidth().weight(1f),
                verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
            ) {
                items(recipients, key = { it.id }) { recipient ->
                    FileRecipientRow(
                        recipient = recipient,
                        detail = if (mode == FileContactPickerMode.Email) recipient.email.orEmpty() else recipient.phone.orEmpty(),
                        selected = recipient.id in selectedRecipientIds,
                        onToggle = { onToggleRecipient(recipient.id) }
                    )
                }
                if (selectedFilter == FileRecipientFilter.All && hasMoreRecipients) {
                    item("load-more-share-recipients") {
                        LaunchedEffect(recipients.size, query) {
                            onLoadMoreRecipients()
                        }
                        HavnSkeletonListFooter()
                    }
                } else if (isLoadingMore) {
                    item("loading-more-share-recipients") {
                        HavnSkeletonListFooter()
                    }
                }
            }
        }
        HavnButton(
            text = "Send",
            onClick = { onSend(selectedRecipients) },
            enabled = selectedRecipients.isNotEmpty(),
            modifier = Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Send
        )
    }
}

@Composable
private fun FileRecipientRow(
    recipient: HavnAttachmentRecipient,
    detail: String,
    selected: Boolean,
    onToggle: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.card,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent.copy(alpha = 0.8f) else theme.border),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onToggle)
    ) {
        Row(modifier = Modifier.padding(HavnSpacing.sm), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Checkbox(
                checked = selected,
                onCheckedChange = { onToggle() },
                colors = CheckboxDefaults.colors(
                    checkedColor = theme.primaryAccent,
                    uncheckedColor = theme.border,
                    checkmarkColor = theme.background
                )
            )
            HavnIcon(
                if (recipient.source == HavnAttachmentRecipientSource.Agent) HavnIconKey.User else HavnIconKey.ContactCard,
                contentDescription = null,
                tint = theme.primaryAccent,
                modifier = Modifier.size(18.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(recipient.displayName, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(detail.ifBlank { recipient.contactLine }, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

private enum class FileContactPickerMode {
    Email,
    Sms
}

private enum class FileRecipientFilter(val label: String) {
    All("All"),
    Recent("Recent")
}

private fun List<HavnAttachmentRecipient>.dedupeRecipients(): List<HavnAttachmentRecipient> =
    distinctBy { it.id }

private fun Set<String>.toggle(value: String): Set<String> =
    if (value in this) this - value else this + value

private fun HavnAttachment.matchesFileSearch(query: String): Boolean {
    val normalized = query.trim()
    if (normalized.isBlank()) return true
    return listOf(fileName, contentType.orEmpty(), contextLabel())
        .any { it.contains(normalized, ignoreCase = true) }
}

private fun HavnAttachment.contextLabel(): String = when {
    propertyId != null && taskId != null -> "Property task file"
    propertyId != null -> "Property file"
    taskId != null -> "Task file"
    else -> "File"
}

private fun String?.shortDateLabel(): String =
    this?.readableFileDate().orEmpty()

private fun String.readableFileDate(): String {
    val date = substringBefore("T").takeIf { it.isNotBlank() } ?: return this
    val parts = date.split("-")
    if (parts.size != 3) return date
    val month = parts[1].toIntOrNull()?.let { fileMonthNames.getOrNull(it - 1) } ?: return date
    val day = parts[2].toIntOrNull() ?: return date
    return "$month $day"
}

private val fileMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

private fun HavnAttachment.sizeLabel(): String = when {
    sizeBytes >= 1024L * 1024L -> "${(sizeBytes / (1024.0 * 1024.0)).formatOneDecimal()} MB"
    sizeBytes >= 1024L -> "${(sizeBytes / 1024.0).formatOneDecimal()} KB"
    else -> "$sizeBytes B"
}

private fun Double.formatOneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    val text = rounded.toString()
    return if (text.endsWith(".0")) text.dropLast(2) else text
}
